import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/use-subscription";
import { TeamLimitUpgradeDialog } from "@/components/subscription/team-limit-upgrade-dialog";
import { UpgradePrompt } from "@/components/subscription/upgrade-prompt";
// employeeStatusEnum import removed since status field is no longer used
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingButton } from "../../components/ui/loading-button";

// Form schema - status field removed since all listed users are considered active
const employeeFormSchema = z.object({
  fullName: z.string()
    .trim()
    .min(1, "Full name is required")
    .refine(val => val.trim().length > 0, "Full name cannot be only spaces"),
  email: z.string()
    .trim()
    .email("Invalid email format")
    .min(1, "Email is required")
    .refine(val => val.trim().length > 0, "Email cannot be only spaces"),
  jobTitle: z.string().optional().transform(val => val?.trim()),
  // Department field removed as part of organization structure simplification
});

type EmployeeFormValues = z.infer<typeof employeeFormSchema>;

type EditEmployeeDialogProps = {
  employeeId: number | null;
  isOpen: boolean;
  onClose: () => void;
};

export default function EditEmployeeDialog({
  employeeId,
  isOpen,
  onClose,
}: EditEmployeeDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditMode = employeeId !== null;
  const { canAddPeople, isAtPeopleLimit, usage, planType, limits } = useSubscription();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  // Define the app setting type
  type AppSetting = {
    id: number;
    type: string;
    value: string;
    display: string;
    isActive: boolean;
  };
  
  // Status options removed since all listed users are considered active

  // Form setup
  const form = useForm<EmployeeFormValues>({
    resolver: zodResolver(employeeFormSchema),
    defaultValues: {
      fullName: "",
      email: "",
      jobTitle: "",
      // Department field removed - status field also removed
    },
  });

  // Fetch employee data if in edit mode
  const { data: employee, isLoading: isLoadingEmployee } = useQuery<{
    id: number;
    fullName: string;
    email: string;
    jobTitle?: string;
    // Department field removed as part of organization structure simplification
    // status field removed since all listed users are considered active
  }>({
    queryKey: [`/api/employees/${employeeId}`],
    enabled: isOpen && isEditMode && !!employeeId,
  });

  console.log("Edit mode:", isEditMode, "Employee ID:", employeeId, "Employee data:", employee);

  // Reset form when dialog opens/closes or when switching between create/edit modes
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && employee) {
        // Edit mode: populate with employee data
        form.reset({
          fullName: employee.fullName || "",
          email: employee.email || "",
          jobTitle: employee.jobTitle || "",
        });
      } else if (!isEditMode) {
        // Create mode: reset to empty values
        form.reset({
          fullName: "",
          email: "",
          jobTitle: "",
        });
      }
    } else {
      // Dialog closed: always reset to clean state
      form.reset({
        fullName: "",
        email: "",
        jobTitle: "",
      });
    }
  }, [isOpen, isEditMode, employee, form]);

  // Create employee mutation
  const createEmployeeMutation = useMutation({
    mutationFn: async (values: EmployeeFormValues) => {
      return apiRequest("/api/employees", { 
        method: "POST", 
        body: values 
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/employees'] });
      toast({
        title: "Employee created",
        description: "The employee has been created successfully.",
      });
      onClose();
    },
    onError: (error: any) => {
      const errorMessage = error?.message || "Failed to create employee. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Update employee mutation
  const updateEmployeeMutation = useMutation({
    mutationFn: async (values: EmployeeFormValues) => {
      if (!employeeId) throw new Error("Employee ID is required");
      return apiRequest(`/api/employees/${employeeId}`, {
        method: "PATCH",
        body: values
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/employees'] });
      queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}`] });
      toast({
        title: "Employee updated",
        description: "The employee has been updated successfully.",
      });
      onClose();
    },
    onError: (error: any) => {
      const errorMessage = error?.message || "Failed to update employee. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Handle form submission
  function onSubmit(values: EmployeeFormValues) {
    if (isEditMode) {
      updateEmployeeMutation.mutate(values);
    } else {
      // Check subscription limits before creating new employee
      if (!canAddPeople()) {
        // Show upgrade dialog for Team plan users
        if (planType === 'team') {
          setShowUpgradeDialog(true);
          return;
        }
        
        // Fallback toast for other plans
        toast({
          title: "User limit reached",
          description: "Please upgrade your plan to add more employees.",
          variant: "destructive",
        });
        return;
      }
      createEmployeeMutation.mutate(values);
    }
  }

  const isPending = createEmployeeMutation.isPending || updateEmployeeMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit" : "Create"} Employee</DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? "Update the employee information below."
              : "Create a new employee record with the information below."
            }
          </DialogDescription>
        </DialogHeader>

        {/* Show upgrade prompt if user limit is reached and trying to create new employee */}
        {!isEditMode && !canAddPeople() && (
          <UpgradePrompt 
            feature="more employees" 
            limitType="people" 
            variant="card" 
            className="mb-4"
          />
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name*</FormLabel>
                  <FormControl>
                    <Input placeholder="John Smith" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email*</FormLabel>
                  <FormControl>
                    <Input placeholder="employee@company.com" type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="jobTitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Software Engineer" {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Department field removed as part of organizational structure simplification */}

            {/* Status field removed since all listed users are considered active */}

            <DialogFooter>
              <Button variant="outline" type="button" onClick={onClose}>
                Cancel
              </Button>
              <LoadingButton 
                type="submit" 
                isLoading={isPending}
                disabled={!isEditMode && !canAddPeople()}
              >
                {isEditMode ? "Update" : "Create"} Employee
              </LoadingButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
      
      {/* Team Limit Upgrade Dialog */}
      <TeamLimitUpgradeDialog
        isOpen={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        currentUserCount={usage?.users || 0}
        onUpgradeSuccess={() => {
          setShowUpgradeDialog(false);
          // Optionally refresh subscription data
        }}
      />
    </Dialog>
  );
}